import React from 'react';
import { Box, Card, CardContent, List, ListItem, ListItemAvatar, Avatar, ListItemText, Typography, Link as MuiLink, Chip, IconButton } from '@mui/material';
import LabelImportantOutlinedIcon from '@mui/icons-material/LabelImportantOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { ICONS } from './iconsRegistry';

// ICONS imported from iconsRegistry

export type ListItemData = {
  text: string;
  subtext?: string;
  href?: string;
  icon?: string;
  iconName?: string;
  image?: string;
  mediaType?: 'icon' | 'image';
  tag?: string;
};

export function ListBuilder({
  title,
  items,
  itemSize,
  iconColor,
  iconBg,
  showSettingsIcon,
  fullSize,
  onOpenSettings,
  scrollDir,
}: {
  title?: string;
  items: ListItemData[];
  itemSize?: number;
  iconColor?: string;
  iconBg?: string;
  showSettingsIcon?: boolean;
  fullSize?: boolean;
  onOpenSettings?: () => void;
  scrollDir?: 'vertical' | 'horizontal';
}) {
  const safeItems = Array.isArray(items) ? items : [];
  const sz = Math.max(20, Math.min(160, Number(itemSize) || 40));
  const gap = Math.max(8, Math.min(24, Math.round(sz * 0.35)));
  const horizontal = scrollDir === 'horizontal';
  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 12px 32px rgba(2,6,23,0.10)', position: 'relative', height: fullSize ? '100%' : 'auto' }}>
      {showSettingsIcon ? (
        <Box sx={{ position: 'absolute', top: fullSize ? 44 : 8, right: 8, zIndex: 1 }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onOpenSettings && onOpenSettings(); }}>
            <SettingsOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : null}
      <CardContent sx={{ height: fullSize ? '100%' : 'auto', display: 'flex', flexDirection: 'column' }}>
        {title ? (
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>
        ) : null}
        <List
          sx={{
            ...(horizontal
              ? {
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'stretch',
                  gap: `${gap}px`,
                  overflowX: fullSize ? 'auto' : 'visible',
                  overflowY: 'hidden',
                  py: 0.5,
                }
              : {
                  overflowY: fullSize ? 'auto' : 'visible',
                  overflowX: 'hidden',
                }),
          }}
        >
          {safeItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Keine Einträge. Füge unten neue Listenelemente hinzu.</Typography>
          ) : (
            safeItems.map((it, idx) => {
              const iconUrl = typeof it.icon === 'string' && it.icon ? it.icon : '';
              const iconName = typeof (it as any).iconName === 'string' ? (it as any).iconName : '';
              const img = typeof (it as any).image === 'string' ? (it as any).image : '';
              const mediaType = (it as any).mediaType === 'image' ? 'image' : 'icon';
              return (
                <ListItem
                  key={idx}
                  sx={{
                    px: horizontal ? 1 : 0,
                    py: 1,
                    alignItems: 'center',
                    ...(horizontal
                      ? { flex: '0 0 auto', width: 'auto', minWidth: Math.max(180, Math.round(sz * 3)) }
                      : {}),
                  }}
                >
                  <ListItemAvatar>
                    {mediaType === 'image' && img ? (
                      <Avatar src={img} alt={it.text || `Item ${idx+1}`} sx={{ bgcolor: 'transparent', width: sz, height: sz }} />
                    ) : iconName && ICONS[iconName] ? (
                      <Avatar sx={{ width: sz, height: sz, bgcolor: iconBg || 'background.paper' }}>
                        <Box sx={{ '& svg': { fontSize: Math.round(sz * 0.62), color: iconColor || 'inherit' } }}>
                          {ICONS[iconName]}
                        </Box>
                      </Avatar>
                    ) : iconUrl ? (
                      <Avatar src={iconUrl} alt={it.text || `Item ${idx+1}`} sx={{ width: sz, height: sz }} />
                    ) : (
                      <Avatar sx={{ width: sz, height: sz, bgcolor: iconBg || 'background.paper' }}>
                        <LabelImportantOutlinedIcon sx={{ color: iconColor || 'inherit', fontSize: Math.round(sz * 0.62) }} />
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <Box sx={{ ml: `${gap}px`, minHeight: sz, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {it.href ? (
                        <MuiLink href={it.href} target="_blank" rel="noopener" underline="hover" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {it.text || `Item ${idx+1}`}
                        </MuiLink>
                      ) : (
                        <Typography fontWeight={700} sx={{ lineHeight: 1.2 }}>{it.text || `Item ${idx+1}`}</Typography>
                      )}
                      {it.tag ? <Chip size="small" label={it.tag} /> : null}
                    </Box>
                    {it.subtext ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.35 }}>{it.subtext}</Typography>
                    ) : null}
                  </Box>
                </ListItem>
              );
            })
          )}
        </List>
      </CardContent>
    </Card>
  );
}
